import Vuex from 'vuex';
import {AdministrativeContactPoint, SignatureData, SignatureDocumentViewModel} from './classes';
import {ContactPointSystem, ContactPointUse, SignatureDocumentFieldType} from './enums';
import { z } from 'zod';
import { IntakeSignatureStep } from '@/components/portal/intake/portalIntakeSchemas';
import { FieldValidationResult, SchemaValidationResult } from '@/components/common/FormValidation';
import {Ref} from "vue";

class PortalIntakeStoreState {
    public telecom: AdministrativeContactPoint[] = [];
    public signatureDocuments: SignatureDocumentViewModel[] = [];
    public signatureValidationState: SchemaValidationResult<z.infer<typeof IntakeSignatureStep>>;
    public patientSignature: SignatureData = new SignatureData();
    public patientInitials: SignatureData = new SignatureData();
}

const portalIntakeStore = new Vuex.Store({
    state: new PortalIntakeStoreState(),
    actions: {
        setTelecom({ commit }, telecom: AdministrativeContactPoint[]) {
            commit('setTelecom', telecom);
        },
        setSignatureValidationState({ commit }, newState: SchemaValidationResult<z.infer<typeof IntakeSignatureStep>>) {
            commit('setSignatureValidationState', newState);
        },
        setPatientSignature({commit}, patientSignature) {
            commit('setPatientSignature', patientSignature)
        },
        setPatientInitials({commit}, patientInitials) {
            commit('setPatientInitials', patientInitials)
        },
        setDocument({commit, state}, {docIndex, required} : {docIndex: number, required: boolean}) {
            const docs = [...state.signatureDocuments];
            if (!docs[docIndex])
                docs[docIndex] = { fields: [], required: false };
            docs[docIndex].required = required;
            commit('setSignatureDocuments', docs);
        },
        setDocumentField({commit, state}, {docIndex, fieldIndex, field} : {docIndex: number, fieldIndex: number, field: {type: SignatureDocumentFieldType, value: Ref<string|undefined>}}) {
            const docs = [...state.signatureDocuments];
            if (!docs[docIndex])
                docs[docIndex] = { fields: [], required: false };
            docs[docIndex].fields[fieldIndex] = field;
            commit('setSignatureDocuments', docs);
        },
    },
    getters: {
        getTelecomContactPoint: (state) => (contactPointUse?: ContactPointUse) => {
            if (!contactPointUse) { // default to email
                return state.telecom.find(x => x.contactPointSystem === ContactPointSystem.Email)
                    ?? new AdministrativeContactPoint(ContactPointSystem.Email, ContactPointUse.Home)
            }

            const contactPoint = state.telecom.find(x => x.contactPointSystem === ContactPointSystem.Phone && x.contactPointUse === contactPointUse)
                ?? new AdministrativeContactPoint(ContactPointSystem.Phone, contactPointUse)

            contactPoint.value = contactPoint.value.startsWith('+1') ? contactPoint.value.substr(1, contactPoint.value.length) : contactPoint.value;
            return contactPoint;
        },
        getSignatureFieldValidationState: (state) => (params: { docIndex: number; fieldIndex: number }): unknown & FieldValidationResult => {
            const document = state.signatureValidationState?.docs?.[params.docIndex];
            return document?.fields?.[params.fieldIndex];
        },
    },
    mutations: {
        setTelecom(state, telecom: AdministrativeContactPoint[]) {
            state.telecom = telecom;
        },
        setSignatureValidationState(state, newState: SchemaValidationResult<z.infer<typeof IntakeSignatureStep>>) {
            state.signatureValidationState = newState;
        },
        setPatientSignature(state, patientSignature: SignatureData) {
            state.patientSignature = patientSignature;
        },
        setPatientInitials(state, patientInitials: SignatureData) {
            state.patientInitials = patientInitials;
        },
        setSignatureDocuments(state, signatureDocuments) {
            state.signatureDocuments = signatureDocuments;
        },
    }
})
export default portalIntakeStore;
export const usePortalIntakeStore = () => portalIntakeStore;